'use client';

import { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import dynamic from 'next/dynamic';

const AIAssistantModal = dynamic(() => import('./AIAssistantModal'), {
  ssr: false,
});

export default function AIAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const pathname = usePathname();
  const isDashboard = pathname.includes('/dashboard');

  useEffect(() => {
    setMounted(true);
  }, []);

  // Hide AI Assistant on login/register pages
  const isHiddenPage = pathname.includes('/login') || pathname.includes('/register');

  // Allow any component to open the modal via a custom DOM event
  useEffect(() => {
    const handler = () => setIsOpen(true);
    window.addEventListener('open-ai-assistant', handler);
    return () => window.removeEventListener('open-ai-assistant', handler);
  }, []);

  if (isHiddenPage && !isOpen) return null;

  return (
    <>
      <div className={cn(
        "fixed right-6 z-[99] flex flex-col items-end gap-3 pointer-events-none transition-all duration-300",
        isDashboard ? "bottom-24 md:bottom-6" : "bottom-6"
      )}>
        <div
          className={cn(
            "transition-all duration-300 transform",
            isOpen ? "scale-0 opacity-0 pointer-events-none" : "scale-100 opacity-100 pointer-events-auto"
          )}
        >
          <Button
            onClick={() => setIsOpen(true)}
            className={cn(
              "group relative h-12 w-12 rounded-full p-0 shadow-2xl transition-all duration-200",
              "bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600",
              "hover:shadow-purple-500/20 hover:ring-2 hover:ring-purple-500/20 hover:scale-110 active:scale-95",
              "text-white border-none flex items-center justify-center"
            )}
            aria-label="Open AI Assistant"
          >
            <Sparkles className="h-5 w-5 animate-pulse" />
            <span className="pointer-events-none absolute bottom-full right-0 mb-2 rounded-lg border border-white/10 bg-slate-900 px-2.5 py-1 text-xs font-medium text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100">
              AI Assistant
            </span>
          </Button>
        </div>
      </div>

      {mounted && (
        <AIAssistantModal 
          isOpen={isOpen} 
          onClose={() => setIsOpen(false)} 
        />
      )}
    </>
  );
}
